import { BaseResourceManager } from '../BaseResourceManager';
import { FieldTranslator } from '../FieldTranslator';
import { TaskManager } from '../TaskManager';
import { executeWithRetry } from '../utils';
import { useReso } from '../../contexts/ResoContext';
import { SearchAgentResult } from '../../system/search';

/**
 * Interface for Agent resource
 */
export interface Agent extends SearchAgentResult {
  // Methods to implement ResoResourceBase interface
  getKey(): string | number | undefined;
  getItemNumber(): number | undefined;
  getListingKeyField(): string | undefined;
  getListingKey(): string | undefined;
}

/**
 * Implementation of Agent resource
 */
export class AgentResource implements Agent {
  // Include all properties from SearchAgentResult
  JobTitle: string | null = null;
  MemberAddress1: string = '';
  MemberAddress2: string | null = null;
  MemberBoxNumber: string | null = null;
  MemberCity: string = '';
  MemberCountry: string = '';
  MemberCounty: string = '';
  MemberDesignation: string[] = [];
  MemberDirectPhone: string = '';
  MemberEmail: string = '';
  MemberFax: string | null = null;
  MemberFirstName: string = '';
  MemberFullName: string = '';
  MemberFullRoleList: string = '';
  MemberJoinDate: string = '';
  MemberKey: string = '';
  MemberLastName: string = '';
  MemberLicenseExpirationDate: string | null = null;
  MemberLoginId: string = '';
  MemberMiddleInitial: string | null = null;
  MemberMiddleName: string | null = null;
  MemberMlsId: string = '';
  MemberMobilePhone: string = '';
  MemberNamePrefix: string | null = null;
  MemberNameSuffix: string | null = null;
  MemberNationalAssociationId: string | null = null;
  MemberNickname: string = '';
  MemberNumViolations: number = 0;
  MemberOfficePhone: string | null = null;
  MemberOfficePhoneExt: string | null = null;
  MemberPager: string | null = null;
  MemberPostalCode: string = '';
  MemberPostalCodePlus4: string | null = null;
  MemberPreferredPhone: string = '';
  MemberPreferredPhoneExt: string | null = null;
  MemberPrivateEmail: string = '';
  MemberRatePlugFlag: boolean | null = null;
  MemberReinstatementDate: string | null = null;
  MemberRoleList: string = '';
  MemberStateLicense: string | null = null;
  MemberStateLicenseState: string | null = null;
  MemberStateOrProvince: string = '';
  MemberStatus: string = '';
  MemberStreetDirSuffix: string | null = null;
  MemberStreetException: string | null = null;
  MemberStreetName: string = '';
  MemberStreetNumber: number = 0;
  MemberStreetSuffix: string = '';
  MemberTerminationDate: string | null = null;
  MemberType: string = '';
  MemberSubType: string = '';
  MemberUnitDesignation: string | null = null;
  MemberUnitNumber: string | null = null;
  MemberVoiceMailExt: string | null = null;
  MemberVoiceMail: string | null = null;
  ModificationTimestamp: string = '';
  OfficeKey: string = '';
  OfficeMlsId: string = '';
  OfficeBrokerKey: string | null = null;
  OfficeName: string = '';
  OfficeBrokerMlsId: string | null = null;
  MemberDateAdded: string = '';
  SocialMediaBlogUrlOrId: string | null = null;
  SocialMediaFacebookUrlOrId: string | null = null;
  SocialMediaLinkedInUrlOrId: string | null = null;
  SocialMediaTwitterUrlOrId: string | null = null;
  SocialMediaWebsiteUrlOrId: string | null = null;
  SocialMediaYouTubeUrlOrId: string | null = null;
  MemberSourceInput: string = '';
  MemberSourceRecordKey: string | null = null;
  MemberSourceRecordID: string | null = null;
  MemberSourceBusinessPartner: string = '';
  MemberSourceTransport: string = '';
  SyndicateTo: string[] = [];
  MemberSubSystemLocale: string = '';
  MemberSystemLocale: string = '';
  MemberPreferredFirstName: string = '';
  MemberPreferredLastName: string = '';
  MemberBrightConvertedYN: boolean = false;
  MemberAssociationPrimary: string | null = null;
  MemberAssociationsFullList: string[] = [];
  MemberPreviewYN: boolean = false;
  SourceModificationTimestamp: string = '';
  MemberStreetDirPrefix: string | null = null;
  DefaultListingShowingAppId: string = '';
  AgentPhoto?: string | null = null;

  constructor(data: Partial<SearchAgentResult> = {}) {
    // Copy all provided data to this instance
    Object.assign(this, data);
  }

  // Implement ResoResourceBase interface methods
  getKey(): string | number | undefined {
    return this.MemberKey;
  }

  getItemNumber(): number | undefined {
    return undefined; // Agents don't have item numbers
  }

  getListingKeyField(): string | undefined {
    return undefined; // Agents don't have listing keys
  }

  getListingKey(): string | undefined {
    return undefined; // Agents don't have listing keys
  }

  // Helper methods
  getFullName(): string {
    return this.MemberFullName;
  }

  getEmail(): string {
    return this.MemberEmail;
  }

  getPhone(): string {
    return (
      this.MemberPreferredPhone ||
      this.MemberMobilePhone ||
      this.MemberDirectPhone ||
      ''
    );
  }

  getProfilePicture(): string | null {
    return this.AgentPhoto || null;
  }

  getOffice(): { key: string; name: string; mlsId: string } {
    return {
      key: this.OfficeKey,
      name: this.OfficeName,
      mlsId: this.OfficeMlsId,
    };
  }

  getAddress(): string {
    const parts = [
      this.MemberAddress1,
      this.MemberAddress2,
      `${this.MemberCity}, ${this.MemberStateOrProvince} ${this.MemberPostalCode}`,
    ].filter(Boolean);

    return parts.join(', ');
  }
}

/**
 * Resource manager for Agent resources
 */
export class AgentResourceManager extends BaseResourceManager<AgentResource> {
  constructor(
    executeQuery: ReturnType<typeof useReso>['executeQuery'],
    fieldTranslator: FieldTranslator,
    taskManager: TaskManager,
    options: {
      enableCwa?: boolean;
      useTranslation?: boolean;
    } = {},
  ) {
    super('Agent', executeQuery, fieldTranslator, taskManager, options);
  }

  /**
   * Map API data to AgentResource
   */
  protected mapFromApiCore(apiData: any): AgentResource {
    return new AgentResource(apiData);
  }

  /**
   * Map AgentResource to API data
   */
  protected mapToApiCore(resource: AgentResource): any {
    // Return a clean copy of the resource for API
    return { ...resource };
  }

  /**
   * Search for agents by name
   */
  public async searchByName(name: string): Promise<AgentResource[]> {
    return this.taskManager.trackTask(
      this.resourceType,
      'other',
      { taskName: 'searchByName' },
      async (taskId) => {
        try {
          // Handle empty search
          if (!name || name.trim() === '') {
            return [];
          }

          // Normalize search term
          const searchTerm = name.trim().toLowerCase();

          // Execute query with retry
          const response: any = await executeWithRetry(
            async () => {
              return this.executeQuery(this.getEntityName(), {
                queryParams: {
                  $filter: `startsWith(MemberFullName), '${searchTerm}')`,
                  $format: 'json',
                },
              });
            },
            { maxRetries: 3 },
          );

          if (!response?.value || !Array.isArray(response.value)) {
            return [];
          }

          return response.value.map((item: any) => this.mapFromApi(item));
        } catch (error) {
          console.error('Error searching agents by name:', error);
          return [];
        }
      },
    );
  }

  /**
   * Get agent by MLS ID
   */
  public async getByMlsId(mlsId: string): Promise<AgentResource | null> {
    return this.findByField('MemberMlsId', mlsId);
  }

  /**
   * Get agent by email
   */
  public async getByEmail(email: string): Promise<AgentResource | null> {
    return this.findByField('MemberEmail', email);
  }
}

/**
 * Create a new AgentResource with default values
 */
export function createAgentResource(
  data: Partial<SearchAgentResult> = {},
): AgentResource {
  return new AgentResource(data);
}
